import React, { memo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Button, Checkbox, Link } from '@box/blueprint-web';
import classnames from 'classnames';
import isAnonymousUser from '../../../common/utils/isAnonymousUser';
import { withCurrentUser } from '../../../common/current-user';
import css from './form-submit-action.module.scss';
import messages from './messages';
import type { User } from '../../../types/User';

type Props = {
  currentUser: User;
  displayNewSubmitterDesign?: boolean;
  isAuthenticationRequired: boolean;
  isDisabled?: boolean;
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  onSubmit: any;
  showTOSFooter: boolean;
  submissionFailed: boolean;
  submitInProgress: boolean;
  tosAcceptanceChecked: boolean;
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  handleTosAcceptanceChange: any;
};

const FormSubmitAction = memo<Props>((props: Props) => {
  const {
    isAuthenticationRequired,
    isDisabled,
    onSubmit,
    submitInProgress,
    submissionFailed,
    currentUser,
    displayNewSubmitterDesign = false,
    showTOSFooter,
    tosAcceptanceChecked,
    handleTosAcceptanceChange,
  } = props;

  const intl = useIntl();

  const tosAcceptanceFooter = (
    <div
      className={css.tosAcceptanceFooter}
      data-testid="form-submit-action-tos-acceptance-footer"
    >
      {/*
       // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322 */}
      <Checkbox.Item
        onCheckedChange={handleTosAcceptanceChange}
        checked={tosAcceptanceChecked}
        data-testid="forms-submit-agree-tos-checkbox"
        label={
          <FormattedMessage
            {...messages.footerCheckboxAgreeTOS}
            values={{
              a: msg => (
                <Link
                  target="_blank"
                  href="https://www.box.com/legal/termsofservice"
                  rel="noreferrer"
                >
                  {msg}
                </Link>
              ),
              b: msg => (
                <Link
                  target="_blank"
                  href="https://www.box.com/legal/privacypolicy"
                  rel="noreferrer"
                >
                  {msg}
                </Link>
              ),
            }}
          />
        }
      />
    </div>
  );
  let submitButtonDisabled = isDisabled || submitInProgress || submissionFailed;
  if (showTOSFooter) {
    submitButtonDisabled = submitButtonDisabled || !tosAcceptanceChecked;
  }

  return (
    <div
      className={classnames({
        [css.newActionContainer]: displayNewSubmitterDesign,
        [css.actionContainer]: !displayNewSubmitterDesign,
      })}
      data-testid="form-submit-action-container"
    >
      {isAuthenticationRequired && !isAnonymousUser(currentUser) && (
        <div className={css.submissionDetail}>
          <FormattedMessage
            {...messages.footerIsAuthenticationRequired}
            values={{
              email: <b>{currentUser.email}</b>,
              a: chunks => <Link href="/logout">{chunks}</Link>,
            }}
          />
        </div>
      )}
      {showTOSFooter && tosAcceptanceFooter}
      <div className={css.buttonContainer}>
        <Button
          disabled={submitButtonDisabled}
          loading={submitInProgress}
          loadingAriaLabel={intl.formatMessage(messages.submitButtonLabel)}
          onClick={onSubmit}
          size="small"
          type="submit"
          variant="primary"
        >
          {intl.formatMessage(messages.submitButtonLabel)}
        </Button>
      </div>
    </div>
  );
});

export default withCurrentUser(FormSubmitAction);
